import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import isUndefined from 'lodash/isUndefined';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles, Button } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";
import Footer from '../../Footer';

import { CHART_QUOTA_DAILY, CHART_QUOTA_HOURLY, CHART_QUOTA_MONTHLY } from '../../../constants/charts';
import {
  FILTER_ENTITY_TYPE_API,
  FILTER_ENTITY_TYPE_API_PER_ORG,
  FILTER_ENTITY_TYPE_ORG,
  USER_FILTER_METRICS,
  USER_FILTER_DATE,
} from '../../../constants/filter';
import { REPORT_QUOTA } from '../../../constants/reports';

import {
  decodeMetricsFilter, decodeDateFilter,
  getShorthandDuration, getSelectedFilters,
  getSummaryFromMetricsFilters, getSummaryFromDateFilter,
} from '../utils/filter';
import {
  getAggregation, getHourlyAggregation,
  getStartAndEndRangeForLastNDays, getStartAndEndRangeWithTimeForLastNDays,
  convertWeekRangeToCustom, getMonthlyUsageAggregation,
} from '../utils';

import UsageFilterWrapper from '../UsageFilterWrapper';
import CustomDateRangeFilter from '../CustomDateRangeFilter';

import {
  API_BASED_USAGE,
  API_PER_ORG_BASED_USAGE,
  ORG_BASED_USAGE,
  TYPE_BY_DAY,
} from '../../../constants/metrics';

import styles from './styles';

const timerangeMap = {
  HOUR_1: 'hour',
  HOUR_24: 'day',
  DAY_7: 'week',
};

const usageTypeMap = {
  [FILTER_ENTITY_TYPE_API]: API_BASED_USAGE,
  [FILTER_ENTITY_TYPE_ORG]: ORG_BASED_USAGE,
  [FILTER_ENTITY_TYPE_API_PER_ORG]: API_PER_ORG_BASED_USAGE,
};

const Usage = (props) => {
  const {
    type,
    charts,
    apiMap,
    apiPerOrgList,
    orgMap,
    selectedMetrics,
    selectedDate,
    isDeveloper,
    isOrgAdmin,
    isOrgPublisher,
    orgUuid,
    userId,
    fetchMetricsData,
    setChartParams,
    setUserMetricFilter,
    setUserDateFilter,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  
  const getDefaultParams = () => {
    let defaultParams = {
      timerange: 'week',
      aggregation: 'by-day',
      sortBy: 'date',
      sortOrder: 'desc',
      planType: 'daily',
      limit: [3],
      startDate: '',
      endDate: '',
      usageType: API_BASED_USAGE,
    };
    if (isDeveloper || isOrgAdmin || isOrgPublisher) {
      const orgIdsArr = [orgUuid];
      defaultParams = {
        ...defaultParams,
        orgIds: orgIdsArr.join(','),
        usageType: ORG_BASED_USAGE,
        limit: [1],
      };
    }
    const hourlyUsageparams = {
      ...defaultParams,
      planType: 'hourly',
    };
    const dailyUsageparams = {
      ...defaultParams,
      planType: 'daily',
    };
    const monthlyUsageParams = {
      ...defaultParams,
      planType: 'monthly',
    };
    return { hourlyUsageparams, dailyUsageparams, monthlyUsageParams };
  };

  const [dailyUsageparams, setDailyUsageparams] = useState();
  const [hourlyUsageparams, setHourlyUsageparams] = useState();
  const [monthlyUsageParams, setMonthlyUsageParams] = useState();

  useEffect(() => {
    if (isEmpty(dailyUsageparams)) {
      return;
    }

    const convertedDailyUsageparams = convertWeekRangeToCustom({ ...dailyUsageparams }, true);
    fetchMetricsData(
      CHART_QUOTA_DAILY, convertedDailyUsageparams, 'usage',
      convertedDailyUsageparams.aggregation, convertedDailyUsageparams.usageType,
    );
    setChartParams(CHART_QUOTA_DAILY, convertedDailyUsageparams);
  }, [dailyUsageparams]);

  useEffect(() => {
    if (isEmpty(hourlyUsageparams)) {
      return;
    }

    const convertedHourlyUsageparams = convertWeekRangeToCustom({ ...hourlyUsageparams }, true);
    fetchMetricsData(
      CHART_QUOTA_HOURLY, convertedHourlyUsageparams, 'usage',
      convertedHourlyUsageparams.aggregation, convertedHourlyUsageparams.usageType,
    );
    setChartParams(CHART_QUOTA_HOURLY, convertedHourlyUsageparams);
  }, [hourlyUsageparams]);

  useEffect(() => {
    if (isEmpty(monthlyUsageParams)) {
      return;
    }

    const convertedMonthlyUsageParams = convertWeekRangeToCustom({ ...monthlyUsageParams }, true);
    fetchMetricsData(
      CHART_QUOTA_MONTHLY, convertedMonthlyUsageParams, 'usage',
      convertedMonthlyUsageParams.aggregation, convertedMonthlyUsageParams.usageType,
    );
    setChartParams(CHART_QUOTA_MONTHLY, convertedMonthlyUsageParams);
  }, [monthlyUsageParams]);

  useEffect(() => {
    const metrics = decodeMetricsFilter(selectedMetrics);
    const dateObj = decodeDateFilter(selectedDate);
    const metricParams = getSelectedFilters(metrics);
    const metric = isArray(metrics) && (metrics.length > 0) && metrics[0];

    const defaultEntityType = (isDeveloper || isOrgAdmin || isOrgPublisher)
      ? FILTER_ENTITY_TYPE_ORG : FILTER_ENTITY_TYPE_API;
    const entityType = (metric && metric.entityType) || defaultEntityType;
    metricParams.usageType = usageTypeMap[entityType];
    // buckets are not supported for usage API
    metricParams.buckets = null;

    const { dailyParams, hourlyParams, monthlyParams } = getDateParams(dateObj);
    const newDailyParams = { ...metricParams, ...dailyParams };
    const newHourlyParams = { ...metricParams, ...hourlyParams };
    const newMonthlyParams = { ...metricParams, ...monthlyParams };
   
    const { dailyUsageparams, hourlyUsageparams, monthlyUsageParams } = getDefaultParams();
    setDailyUsageparams({ ...dailyUsageparams, ...newDailyParams });
    setHourlyUsageparams({ ...hourlyUsageparams, ...newHourlyParams });
    setMonthlyUsageParams({ ...monthlyUsageParams, ...newMonthlyParams });
  }, []);


  const getDateParams = (dateObj) => {
    if (isUndefined(dateObj) || isEmpty(dateObj)) {
      return {};
    }
    let hourlyAggregation;
    let dailyAggregation;
    let monthlyAggregation;
    let timerange = 'custom';
    const newParams = {};
    const dailynewParams = {};
    if (!isUndefined(dateObj.customStartDate) && !isUndefined(dateObj.customEndDate)) {
      newParams.startDate = dateObj.customStartDate.format('YYYY-MM-DD');
      newParams.endDate = dateObj.customEndDate.format('YYYY-MM-DD');
      dailyAggregation = getAggregation(timerange, dateObj.customStartDate,
        dateObj.customEndDate);
      hourlyAggregation = getHourlyAggregation(timerange);
      monthlyAggregation = getMonthlyUsageAggregation(timerange, dateObj.customStartDate,
          dateObj.customEndDate);
    } else if (!isUndefined(dateObj.last) && isNumber(dateObj.last)) {
      const customDays = getStartAndEndRangeForLastNDays(dateObj.last);
      const customDaysWithTime = getStartAndEndRangeWithTimeForLastNDays(dateObj.last);
      newParams.startDate = customDays.startDate;
      newParams.endDate = customDays.endDate;
      dailyAggregation = getAggregation(
        timerange, customDaysWithTime.startDate, customDaysWithTime.endDate);
      if (dailyAggregation === TYPE_BY_DAY) {
        dailynewParams.startDate = customDaysWithTime.startDate;
        dailynewParams.endDate = customDaysWithTime.endDate;
      } else {
        dailynewParams.startDate = customDays.startDate;
        dailynewParams.endDate = customDays.endDate;
      }
      hourlyAggregation = getHourlyAggregation(timerange);
      monthlyAggregation =
        getMonthlyUsageAggregation(timerange, customDays.startDate, customDays.endDate);
    } else {
      timerange = timerangeMap[dateObj.last];
      dailyAggregation = getAggregation(timerange);
      hourlyAggregation = getHourlyAggregation(timerange);
      monthlyAggregation = getMonthlyUsageAggregation(timerange);
    }
    newParams.timerange = timerange;
    dailynewParams.timerange = timerange;
    const hourlynewParams = dailynewParams;
    const dailyParams = { ...dailynewParams, aggregation: dailyAggregation };
    const hourlyParams = { ...hourlynewParams, aggregation: hourlyAggregation };
    const monthlyParams = { ...newParams, aggregation: monthlyAggregation };
    return { dailyParams, hourlyParams, monthlyParams };
  }

  const handleDateRangeChange = (customDateRange) => {
    const timerange = !customDateRange.customDays ? customDateRange.unit : 'custom';
    const daysObject = !isUndefined(customDateRange.customDates) ?
      customDateRange.customDates : getStartAndEndRangeForLastNDays(customDateRange.value);
    const daysObjectWithTime = !isUndefined(customDateRange.customDates) ?
      customDateRange.customDates : getStartAndEndRangeWithTimeForLastNDays(customDateRange.value);
    const dailyAggregation = getAggregation(
      timerange, daysObjectWithTime.startDate, daysObjectWithTime.endDate);
    const hourlyAggregation = getHourlyAggregation(timerange);
    const monthlyAggregation =
      getMonthlyUsageAggregation(timerange, daysObject.startDate, daysObject.endDate);

    const updatedDailyParams = {
      ...dailyUsageparams,
      timerange,
      aggregation: dailyAggregation,
    };
    const updatedHourlyParams = {
      ...hourlyUsageparams,
      timerange,
      aggregation: hourlyAggregation,
    };
    const updatedMonthlyParams = {
      ...monthlyUsageParams,
      timerange,
      aggregation: monthlyAggregation,
    };
    if (timerange === 'custom') {
      if (dailyAggregation === TYPE_BY_DAY) {
        updatedDailyParams.startDate = daysObjectWithTime.startDate;
        updatedDailyParams.endDate = daysObjectWithTime.endDate;
        updatedHourlyParams.startDate = daysObjectWithTime.startDate;
        updatedHourlyParams.endDate = daysObjectWithTime.endDate;
      } else {
        updatedDailyParams.startDate = daysObject.startDate;
        updatedDailyParams.endDate = daysObject.endDate;
        updatedHourlyParams.startDate = daysObject.startDate;
        updatedHourlyParams.endDate = daysObject.endDate;
      }
      updatedMonthlyParams.startDate = daysObject.startDate;
      updatedMonthlyParams.endDate = daysObject.endDate;
    }
    setDailyUsageparams(updatedDailyParams);
    setHourlyUsageparams(updatedHourlyParams);
    setMonthlyUsageParams(updatedMonthlyParams);

    if (!isUndefined(customDateRange.customDates)) {
      updateDateFilter({
        start: daysObject.startDate,
        end: daysObject.endDate,
      });
    } else {
      updateDateFilter({ last: getShorthandDuration(customDateRange) });
    }
  };

  const handleFiltersChange = (selectedFilters) => {
    const updatedParams = {};
    updatedParams.apiIds = null;
    updatedParams.orgIds = null;
    updatedParams.apiperorgsids = null;
    updatedParams.limit = selectedFilters.limit;
    if (selectedFilters.entityType === FILTER_ENTITY_TYPE_API) {
      updatedParams.apiIds = selectedFilters.apiIds;
      updatedParams.usageType = API_BASED_USAGE;
    } else if (selectedFilters.entityType === FILTER_ENTITY_TYPE_ORG) {
      updatedParams.orgIds = selectedFilters.orgIds;
      updatedParams.usageType = ORG_BASED_USAGE;
    } else if (selectedFilters.entityType === FILTER_ENTITY_TYPE_API_PER_ORG) {
      updatedParams.apiIds = selectedFilters.apiIds;
      updatedParams.orgIds = selectedFilters.orgIds;
      updatedParams.usageType = API_PER_ORG_BASED_USAGE;
    }

    const updatedDailyParams = {
      ...dailyUsageparams,
      ...updatedParams,
    };
    const updatedHourlyParams = {
      ...hourlyUsageparams,
      ...updatedParams,
    };
    const updatedMonthlyParams = {
      ...monthlyUsageParams,
      ...updatedParams,
    };
    setDailyUsageparams(updatedDailyParams);
    setHourlyUsageparams(updatedHourlyParams);
    setMonthlyUsageParams(updatedMonthlyParams);
  }

  const handleExport = (e) => {
    window.print();
    e.preventDefault();
    e.target.blur();
    return false;
  };

  const updateMetricFilters = (filtersState) => {
    setUserMetricFilter(userId, orgUuid, USER_FILTER_METRICS, filtersState);
  }

  const updateDateFilter = (dateObj) => {
    setUserDateFilter(userId, orgUuid, USER_FILTER_DATE, dateObj);
  }

  const renderChart = (obj) => {
    const { component: Component, id } = obj;
    return (
      <div className={classes.printchart} key={id}>
        <Component key={id} />
      </div>
    );
  };

  const renderDataFilterSummary = () => {
    const { usageType, apiIds, orgIds, limit } = dailyUsageparams || {};
    if (!dailyUsageparams
      || ((usageType === ORG_BASED_USAGE) && (isDeveloper || isOrgAdmin || isOrgPublisher))) {
      return <div />;
    }
    let summaryTextLines = [];
    if (usageType === API_PER_ORG_BASED_USAGE) {
      summaryTextLines.push(intl.getI18nMessage('label.analytics.print.filters.selected'));
      const hasAPIs = apiIds && apiIds.length;
      const hasOrgs = orgIds && orgIds.length;
      const aPOAPIMap = {};
      const aPOOrgMap = {};
      forEach(apiPerOrgList, item => {
        aPOAPIMap[item.apiUuid] = item.apiName;
        aPOOrgMap[item.organizationUuid] = item.organizationName;
      });
      const apiIdsArray = hasAPIs ? apiIds.split(',') : [];
      const orgIdsArray = hasOrgs ? orgIds.split(',') : [];
      const entitiesArray = [];
      if (hasAPIs && hasOrgs) {
        const numberOfAPIs = apiIdsArray.length;
        const numberOfORGs = orgIdsArray.length;
        if (numberOfAPIs === numberOfORGs) {
          const selectedAPIPerOrgs = [];
          for (let index = 0; index < numberOfAPIs; index++) {
            selectedAPIPerOrgs.push({
              apiName: aPOAPIMap[apiIdsArray[index]],
              organizationName: aPOOrgMap[orgIdsArray[index]],
            });
          }
          forEach(selectedAPIPerOrgs, item => {
            entitiesArray.push(`${item.apiName} - ${item.organizationName}`);
          });
          const currentLine = `api per orgs: ${entitiesArray.join(',')}`;
          summaryTextLines.push(currentLine);
        }
      } else if (hasAPIs && !hasOrgs) {
        forEach(apiIdsArray, id => {
          entitiesArray.push(aPOAPIMap[id]);
        });
        const currentLine = `api per orgs: Top ${limit[0]} orgs with APIs ${entitiesArray.join(',')}`;
        summaryTextLines.push(currentLine);
      } else if (!hasAPIs && hasOrgs) {
        forEach(orgIdsArray, id => {
          entitiesArray.push(aPOOrgMap[id]);
        });
        const currentLine = `api per orgs: Top ${limit[0]} apis with orgs ${entitiesArray.join(',')}`;
        summaryTextLines.push(currentLine);
      } else {
        const currentLine = `api per orgs: Top ${limit[0]}`;
        summaryTextLines.push(currentLine);
      }
    } else {
      let buckets = [];
      if (usageType === API_BASED_USAGE) {
        buckets = ['apis'];
      } else if (usageType === ORG_BASED_USAGE) {
        buckets = ['orgs'];
      }
      const params = {
        ...dailyUsageparams,
        buckets,
      };
      summaryTextLines = getSummaryFromMetricsFilters(params, {
        apiMap,
        orgMap,
      }, intl);
    }

    return summaryTextLines ?
    (<div className={classes.printdataContainer}>
      {map(summaryTextLines,
        line => <div className={classes.printdatafilterelem}>{line}</div>)}
    </div>) :
    null;
  };

  const renderDateFilterSummary = () => {
    if (!dailyUsageparams) {
      return <div />;
    }
    const { timerange, startDate, endDate } = dailyUsageparams;
    const dateObj = {
      timerange,
      startDate,
      endDate,
    };
    const dateFilterSummary = getSummaryFromDateFilter(dateObj, intl);
    return (<div className={classes.printdateContainer}>
      <div className={classes.printdatefilter} >
        {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.label')}
      </div>
      <div>{dateFilterSummary}</div>
    </div>);
  };

  const decodedSelectedMetrics = decodeMetricsFilter(selectedMetrics);
  return (
    <div>
      <div className={classes.dashboardContainer}>
        <div className={classes.dashboard}>
          <div className={classes.dashboardLabelContainer}>
            <div className={classes.dashboardLabel}>
              <div className={classes.dashboardLabelTitle}>
                {intl.getI18nMessage('label.analytics.usage.title.text')}
              </div>
              <div className={classes.placeholderText}>
                {intl.getI18nMessage('label.analytics.quotaConsumption.placeholder.text')}
              </div>
            </div>
            <div className={classes.exportBtnContainer}>
              <Button className={classes.exportBtn} onClick={handleExport} >
                {intl.getI18nMessage('label.analytics.print.button.export')}
              </Button>
            </div>
          </div>
          <div className={clsx(classes.filters, 'SkipPrint')}>
            <UsageFilterWrapper
              apiMap={apiMap}
              apiPerOrgList={apiPerOrgList}
              orgMap={orgMap}
              handleFiltersChange={handleFiltersChange}
              updateMetricFilters={updateMetricFilters}
              selectedMetrics={decodedSelectedMetrics}
              isDeveloper={isDeveloper}
              isOrgAdmin={isOrgAdmin}
              isOrgPublisher={isOrgPublisher}
              userOrgUuid={orgUuid}
            />
            <CustomDateRangeFilter
              handleDateRangeChange={handleDateRangeChange}
              selectedDate={decodeDateFilter(selectedDate)}
            />
          </div>
          <div className={classes.printfiltersummary}>
            {renderDataFilterSummary()}
            {renderDateFilterSummary()}
          </div>
          {type === REPORT_QUOTA &&
            <div className={classes.report}>
              {charts && charts.map(renderChart)}
            </div>
          }
        </div>
      </div>
      <Footer />
    </div>
  );
}

Usage.propTypes = {
  type: PropTypes.string,
  charts: PropTypes.arrayOf(PropTypes.shape({
    component: PropTypes.object,
    id: PropTypes.string,
  })),
  fetchMetricsData: PropTypes.func,
  setChartParams: PropTypes.func,
  apiPerOrgList: PropTypes.arrayOf(PropTypes.object),
  apiMap: PropTypes.object,
  orgMap: PropTypes.object,
  userId: PropTypes.string,
  orgUuid: PropTypes.string,
  setUserMetricFilter: PropTypes.func,
  setUserDateFilter: PropTypes.func,
  selectedMetrics: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.object,
  isDeveloper: PropTypes.bool,
  isOrgAdmin: PropTypes.bool,
  isOrgPublisher: PropTypes.bool,
};

export default Usage;
